import { useEffect, useMemo, useState } from "react";
import { Stack, H3, H4, Button } from "@introist/react-foundation/v2";
import { Form, FormField } from "services/api/PublicFormApi";
import { useObjectForm } from "../FormFields/useObjectForm";
import { Field, HtmlDescription } from "./Field";
import { SmoothCard } from "./SmoothCard";
import { GlobalView } from "./ViewFrame";
import parseHtml from "html-react-parser";
import { FormSubmission } from "modules/forms/routes/public/PublicFormRoute";
import { CustomColorButton } from "./CustomColorButton";
import styled from "styled-components";

const BannerArea = styled.div<{ $backgroundImageUrl: string }>`
  width: 100%;
  height: 35vh;
  background-image: url(${props => props.$backgroundImageUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.15), 0px 2px 8px 0px rgba(0, 0, 0, 0.1);

  /* Very small phones (320px - 479px) */
  @media only screen and (max-width: 479px) {
    height: 25vh;
  }

  /* Standard phones (480px - 767px) */
  @media only screen and (min-width: 480px) and (max-width: 767px) {
    height: 28vh;
  }

  /* Tablets and large phones (768px - 1023px) */
  @media only screen and (min-width: 768px) and (max-width: 1023px) {
    height: 32vh;
  }
`;

const BannerModeStack = styled(Stack)<{ $isBannerMode: boolean }>`
  ${props =>
    props.$isBannerMode &&
    `
    position: relative;
    z-index: 1;
    padding-top: 20vh;

    /* Very small phones (320px - 479px) */
    @media only screen and (max-width: 479px) {
      padding-top: 14vh;
    }

    /* Standard phones (480px - 767px) */
    @media only screen and (min-width: 480px) and (max-width: 767px) {
      padding-top: 16vh;
    }

    /* Tablets and large phones (768px - 1023px) */
    @media only screen and (min-width: 768px) and (max-width: 1023px) {
      padding-top: 18vh;
    }
  `}
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 0.25rem;
  width: 100%;
`;

const ProgressSegment = styled.div<{ $filled: boolean; $accentColor?: string }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background-color: ${props =>
    props.$filled ? props.$accentColor ?? "var(--palette-primary-default)" : "var(--palette-surface-default)"};
  transition: background-color 0.2s ease;
`;

const ResponsiveLogo = styled.img`
  max-width: 150px;
  max-height: 60px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin-bottom: var(--spacing-medium);

  /* Very small phones (320px - 479px) */
  @media only screen and (max-width: 479px) {
    max-width: 100px;
    max-height: 40px;
  }

  /* Standard phones (480px - 767px) */
  @media only screen and (min-width: 480px) and (max-width: 767px) {
    max-width: 120px;
    max-height: 50px;
  }
`;

export const PublicForm = ({
  form,
  onSubmit,
  responseId,
  previewMode = false
}: {
  form: Form;
  onSubmit: (formSubmission: FormSubmission) => unknown;
  responseId: string;
  previewMode?: boolean;
}) => {
  const { value, onChange, hasChanges, errors, submit } = useObjectForm<{ [key: string]: string }>(
    form.values,
    form.values,
    {
      submitErrorMessage: "Submission failed"
    }
  );

  const hasPages = !!form.pages && form.pages.length > 0;

  const pageGroups = useMemo(() => {
    if (!hasPages) return null;
    const pageIds = new Set((form.pages ?? []).map(p => p.id));
    const ungrouped = form.fields.filter(f => !f.pageId || !pageIds.has(f.pageId));

    const groups: { id: string | null; title: string | null; fields: FormField[] }[] = [];
    if (ungrouped.length > 0) groups.push({ id: null, title: null, fields: ungrouped });
    (form.pages ?? []).forEach(page => {
      groups.push({
        id: page.id,
        title: page.title,
        fields: form.fields.filter(f => f.pageId === page.id)
      });
    });
    return groups;
  }, [hasPages, form.fields, form.pages]);

  const [activePageIndex, setActivePageIndex] = useState(0);

  const activeFields = hasPages ? pageGroups![activePageIndex]?.fields ?? [] : form.fields;
  const isFirstPage = !hasPages || activePageIndex === 0;
  const isLastPage = !hasPages || activePageIndex === pageGroups!.length - 1;
  const canProceed = !hasPages || activeFields.filter(f => f.required).every(f => !!value[f.id]);

  // Jump back to the first page containing a field the server rejected on submit
  useEffect(() => {
    if (!hasPages || !pageGroups) return;
    const erroredFieldIds = Object.keys(errors);
    if (erroredFieldIds.length === 0) return;
    const pageIndex = pageGroups.findIndex(g => g.fields.some(f => erroredFieldIds.includes(f.id)));
    if (pageIndex >= 0) setActivePageIndex(pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  // Load Google Font if custom font is selected
  useEffect(() => {
    if (form.branding?.fontFamily) {
      const fontFamily = form.branding.fontFamily;
      const linkId = `google-font-${fontFamily.replace(/\s+/g, "-")}`;

      // Check if font is already loaded
      if (document.getElementById(linkId)) {
        return;
      }

      // Create and append Google Fonts link
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
        / /g,
        "+"
      )}:wght@400;700&display=swap`;
      document.head.appendChild(link);

      // Cleanup on unmount
      return () => {
        const existingLink = document.getElementById(linkId);
        if (existingLink) {
          existingLink.remove();
        }
      };
    }
  }, [form.branding?.fontFamily]);

  const handleSubmit = submit(async values => {
    return onSubmit({ responseId, submission: values });
  });

  const isBannerMode = !!(
    form.branding?.backgroundImageMode === "banner" && form.branding?.backgroundImageUrl
  );
  const isFullMode = !!(
    (form.branding?.backgroundImageMode === "full" || !form.branding?.backgroundImageMode) &&
    form.branding?.backgroundImageUrl
  );

  return (
    <GlobalView
      style={{
        ...(form.branding?.backgroundColor && {
          backgroundColor: form.branding.backgroundColor
        }),
        ...(isFullMode && {
          backgroundImage: `url(${form.branding?.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }),
        ...(isBannerMode && {
          position: "relative",
          justifyContent: "start"
        }),
        ...(form.branding?.fontFamily && {
          // @ts-ignore - Setting CSS variables for font
          "--typography-font-text": `'${form.branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          "--typography-font-heading": `'${form.branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`,
          fontFamily: `'${form.branding.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Helvetica Neue', Arial, sans-serif`
        })
      }}
    >
      {isBannerMode && form.branding?.backgroundImageUrl && (
        <BannerArea $backgroundImageUrl={form.branding.backgroundImageUrl} />
      )}
      <BannerModeStack $isBannerMode={isBannerMode}>
        <SmoothCard>
          {form?.branding?.logoUrl && (
            <ResponsiveLogo src={form.branding.logoUrl} alt="Form logo" />
          )}
          <Stack vertical style={{ width: "100%" }}>
            <H3>{form?.welcomeScreen?.title ?? form?.title}</H3>
            {(form?.welcomeScreen?.description ?? form?.description) && (
              <HtmlDescription>
                {parseHtml(form?.welcomeScreen?.description ?? form?.description)}
              </HtmlDescription>
            )}
          </Stack>
          {hasPages && pageGroups![activePageIndex]?.title && (
            <Stack style={{ width: "100%" }}>
              <H4>{pageGroups![activePageIndex]!.title}</H4>
            </Stack>
          )}
          <Stack vertical gap="xLarge" style={{ width: "100%" }}>
            {activeFields.map(field => (
                <Field
                  key={field.id}
                  field={field}
                  value={value[field.id]}
                  onChange={onChange(field.id)}
                  error={errors[field.id]}
                  responseId={responseId}
                  previewMode={previewMode}
                  primaryColor={form.branding?.primaryColor ?? undefined}
                ></Field>
              ))}
          </Stack>
          {hasPages && pageGroups!.length > 1 && (
            <ProgressBar>
              {pageGroups!.map((_, index) => (
                <ProgressSegment
                  key={index}
                  $filled={index <= activePageIndex}
                  $accentColor={form.branding?.primaryColor ?? undefined}
                />
              ))}
            </ProgressBar>
          )}
          <Stack
            justifyContent="center"
            gap="small"
            style={{ width: "100%", marginTop: "var(--spacing-xLarge)" }}
          >
            {hasPages && !isFirstPage && (
              <Button variant="outlined" onClick={() => setActivePageIndex(i => i - 1)}>
                Back
              </Button>
            )}
            {hasPages && !isLastPage ? (
              form.branding?.primaryColor ? (
                <CustomColorButton
                  disabled={!canProceed}
                  onClick={() => setActivePageIndex(i => i + 1)}
                  startIcon="arrowRight"
                  customColor={form.branding.primaryColor}
                >
                  Next
                </CustomColorButton>
              ) : (
                <Button
                  disabled={!canProceed}
                  onClick={() => setActivePageIndex(i => i + 1)}
                  startIcon="arrowRight"
                >
                  Next
                </Button>
              )
            ) : form.branding?.primaryColor ? (
              <CustomColorButton
                disabled={!hasChanges || !canProceed}
                onClickWithLoading={handleSubmit}
                startIcon="check"
                customColor={form.branding.primaryColor}
              >
                Submit
              </CustomColorButton>
            ) : (
              <Button disabled={!hasChanges || !canProceed} onClickWithLoading={handleSubmit} startIcon="check">
                Submit
              </Button>
            )}
          </Stack>
        </SmoothCard>
      </BannerModeStack>
    </GlobalView>
  );
};
